import { type Router, type LocationQueryRaw, RouteRecordRaw } from 'vue-router';
// import NProgress from 'nprogress'; // progress bar

import { useUserStore, useTabBarStore, useSupplierStore } from '@/store';
import { isLogin } from '@/utils/auth';
import { isLoginBySupplier } from '@/store/modules/supplier/auth';
import {
  getCurrentUser,
  queryCompanyConfigList,
  queryUserConfigList,
} from '@/api/user';
import { thirdpartList } from '@/api/thirdparty';
import { getFlowList } from '@/api/flow';
import {
  SUPPLIER_LOGIN_REDIRECT_ROUTE_NAME,
  DEFAULT_SUPPLIER_ROUTE_NAME,
  SUPPLIER_LOGIN_ROUTE,
  NOT_BOUND_CLIENT_ROUTE_NAME,
  SUPPLIER_LOGIN_ROUTE_NAME,
} from '@/router/constants';
import { getQueryVariable } from '@/utils/common';
import { supplierLogin, getSupplierConfig } from '@/api/wx';
import useSSE from '@/hooks/useSSE';
import { queryRestDays } from '@/api/product';
import dayjs from 'dayjs';
import { isElectronMainPage } from '@/utils/electronHelper';
import { toGrayUrlForGrayUser } from '@/utils/dms';

let isGlobalLoading = true;
let electronInit = false;

// 处理路由菜单隐藏逻辑
const handleRouter = (router: Router) => {
  const { paymentMap } = useTabBarStore();
  const routesConfig = router.getRoutes();
  const handleRoute = (routes: any) => {
    const titleMap: any = {
      '/develop': `研发管理${
        paymentMap?.flagShip && paymentMap?.professional ? '（专业版）' : ''
      }`,
      '/develop-pro': `研发管理${
        paymentMap?.flagShip && paymentMap?.professional ? '（旗舰版）' : ''
      }`,
    };
    routes.forEach((route: any) => {
      if (titleMap[route.path]) {
        route.meta.title = titleMap[route.path];
      }
      if (
        (!paymentMap?.flagShip && route.path === '/develop-pro') ||
        (!paymentMap?.professional && route.path === '/develop')
      ) {
        route.meta.hideInMenu = true;
      }
      if (route.flagShip && !paymentMap?.flagShip) {
        // 未开启旗舰版
        route.meta.hideInMenu = true;
      }
    });
  };
  handleRoute(routesConfig);
};
export default function setupUserLoginInfoGuard(router: Router) {
  const tabbarStore = useTabBarStore();
  const { checkSSEStatus } = useSSE();
  router.beforeEach(async (to, from, next) => {
    // NProgress.start();
    // 添加静态文件例外规则
    // if (window.location.pathname.includes('weChatJumpToApp.html')) {
    //   console.log(123);

    //   next(window.location.pathname);
    //   return;
    // }
    const userStore = useUserStore();
    tabbarStore.setPageLoading(true);
    if (isLogin()) {
      if (!tabbarStore.paymentMap) {
        const currentUser = await getCurrentUser();
        tabbarStore.setPayment(currentUser);
      }
      // 已经请求了currentUser.ac的接口，刷新后，浏览器PC中userStore.userInfo 是undefined,
      // 但是客户端userStore.userInfo是有正常（旧）值的，必须让客户端走一次获取用户信息的逻辑
      // 用 electronInit 做一个阀门，只修改一次。
      let userInfo = { ...(userStore.userInfo ?? {}) };
      if (isElectronMainPage() && !electronInit) {
        userInfo = {};
        electronInit = true;
      }
      if (Object.keys(userInfo).length !== 0) {
        if (to.name === 'login') {
          // 阻止已经登录的用户直接访问登录页
          next(false);
          return;
        }
        next();
      } else {
        try {
          const [
            currentUser,
            companyConfig,
            userConfig,
            thirdpartyRes,
            flowConfigRes,
            restDaysRes,
          ] = await Promise.all([
            getCurrentUser(),
            queryCompanyConfigList(),
            queryUserConfigList({}),
            thirdpartList().catch((error: any) => {
              console.error('thirdpartList error:', error);
              return {};
            }),
            getFlowList({}),
            queryRestDays({}).catch(() => {
              return {};
            }),
          ]);
          await toGrayUrlForGrayUser(currentUser.data?.companyInfo.id);
          // 更新第三方对接信息
          userStore.updateThirdparty(thirdpartyRes?.data?.thirdparts ?? []);
          // 更新流程配置信息
          userStore.updateFlowConfig(flowConfigRes?.data?.list ?? []);
          // 更新休息日信息
          userStore.updateRestDays(restDaysRes.data ?? []);
          checkSSEStatus(); // 检查SSE连接
          const { paymentMap } = tabbarStore;
          userStore.setInfo({
            userInfo: currentUser.data?.userInfo,
            permissions: currentUser.data?.permissions,
            companyInfo: currentUser.data?.companyInfo,
            permissionRemarkMap: currentUser.data.permissionRemarkMap,
            sysConfigList: companyConfig.data,
            userConfigList: userConfig.data,
            enablePaymentServiceMap: paymentMap,
          });
          // 处理菜单路由隐藏
          handleRouter(router);
          next();
        } catch {
          await userStore.logout();
          next({
            name: 'login',
            query: {
              redirect: to.name,
              ...to.query,
            } as LocationQueryRaw,
          });
        }
      }
    } else if (isLoginBySupplier()) {
      // 已登录了加工厂账号
      const supplierStore = useSupplierStore();
      if (Object.keys(supplierStore.supplierInfo ?? {}).length !== 0) {
        if (Object.keys(supplierStore.clientInfo ?? {}).length !== 0) {
          if (supplierStore.sysConfigList.length === 0) {
            const supplierConfig = await getSupplierConfig();
            supplierStore.setInfo({
              sysConfigList: supplierConfig.data?.config,
            });
          }
          if (to.name === SUPPLIER_LOGIN_ROUTE_NAME) {
            // 有账号重定向到首页
            next({ name: DEFAULT_SUPPLIER_ROUTE_NAME });
            return;
          }
          next();
          return;
        }
        if (to.name === NOT_BOUND_CLIENT_ROUTE_NAME) {
          next();
          return;
        }
        // 没绑定品牌方
        next({ name: NOT_BOUND_CLIENT_ROUTE_NAME });
      } else {
        // 未登录加工厂账号
        await supplierStore.logout();
        next(SUPPLIER_LOGIN_ROUTE);
      }
    } else {
      if ([SUPPLIER_LOGIN_ROUTE_NAME, 'login'].includes(to.name as string)) {
        next();
        return;
      }
      if (
        (getQueryVariable('state') as string) ===
        SUPPLIER_LOGIN_REDIRECT_ROUTE_NAME
      ) {
        // 扫码确认登录后，微信服务器的重定向
        const supplierStore = useSupplierStore();
        try {
          // 使用code登录微信账号
          const currentSupplier = await supplierLogin({
            code: getQueryVariable('code'),
          });
          supplierStore.loginCallback(currentSupplier.data);
          const { assistantSupplierList = [] } = currentSupplier.data;
          // 去除哈希#号前多余的字符串并重定向到加工厂系统首页
          const correctUrl = new URL(window.location.href);
          // 拿到路由对应的路径href
          const addHref = router.resolve({
            name: assistantSupplierList.length
              ? DEFAULT_SUPPLIER_ROUTE_NAME
              : NOT_BOUND_CLIENT_ROUTE_NAME,
          });
          // 重定向（没绑定品牌方重定向到未绑定页面）
          window.location = (correctUrl.origin +
            correctUrl.pathname +
            addHref.href) as Location | (string & Location);
          next(false);
        } catch {
          await supplierStore.logout();
          // 跳回加工厂的登录页面
          next(SUPPLIER_LOGIN_ROUTE);
        }
        return;
      }
      // 跳回erp的登录页面
      next({
        name: 'login',
        query: {
          redirect: to.name,
          ...to.query,
        } as LocationQueryRaw,
      });
    }
  });
  router.afterEach(() => {
    if (isGlobalLoading) {
      const globalLoadingDom = document.querySelector('#globalLoading');
      if (globalLoadingDom) {
        globalLoadingDom.remove();
        isGlobalLoading = false;
      }
    }

    setTimeout(() => {
      tabbarStore.setPageLoading(false);
    }, 80);
    // NProgress.done();
  });
}
